import api from "@/utils/axios";

const USER_API_URL = `/user`;

export default {
  namespaced: true,
  state: {
    me: null,
    users: [],
    sessions: [],
    loading: false,
    error: null,
  },
  mutations: {
    SET_ME(state, user) {
      state.me = user;
    },
    SET_USERS(state, users) {
      state.users = users;
    },
    SET_SESSIONS(state, sessions) {
      state.sessions = sessions;
    },
    SET_LOADING(state, status) {
      state.loading = status;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    CLEAR_USER(state) {
      state.me = null;
      state.users = [];
      state.sessions = [];
      state.error = null;
    },
  },
  actions: {
    async fetchMyUser({ commit }) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.get(`${USER_API_URL}/me/`, {
          successNotify: false,
          errorNotify: false,
        });
        commit("SET_ME", res.data.user);
        return res.data;
      } catch (error) {
        commit(
          "SET_ERROR",
          error.response?.data?.error || "Failed to fetch user"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async updateMyUser({ commit }, payload) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.patch(`${USER_API_URL}/me/`, payload);
        commit("SET_ME", res.data.user);
        return res.data;
      } catch (error) {
        commit("SET_ERROR", error.response?.data?.error || "Update failed");
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchMySessions({ commit }) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.get(`${USER_API_URL}/me/sessions/`, {
          successNotify: false,
          errorNotify: false,
        });
        commit("SET_SESSIONS", res.data.sessions);
        return res.data;
      } catch (error) {
        commit(
          "SET_ERROR",
          error.response?.data?.error || "Failed to fetch sessions"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    // Admin-only actions
    async fetchUser(_, userId) {
      try {
        const res = await api.get(`${USER_API_URL}/${userId}/`);
        return res.data.user;
      } catch (error) {
        throw new Error(error.response?.data?.error || "User not found");
      }
    },

    async fetchUserSessions(_, userId) {
      try {
        const res = await api.get(`${USER_API_URL}/${userId}/sessions/`);
        return res.data.sessions;
      } catch (error) {
        throw new Error(
          error.response?.data?.error || "Failed to fetch sessions"
        );
      }
    },

    async filterUsers({ commit }) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.post(`${USER_API_URL}/filter/`);
        commit("SET_USERS", res.data.users);
        return res.data.users;
      } catch (error) {
        commit(
          "SET_ERROR",
          error.response?.data?.error || "Failed to fetch users"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async addNewUser(_, payload) {
      try {
        const res = await api.post(`${USER_API_URL}/new/`, payload);
        return res.data.user;
      } catch (error) {
        throw new Error(error.response?.data?.error || "User creation failed");
      }
    },

    async updateUser(_, { userId, data }) {
      try {
        const res = await api.patch(`${USER_API_URL}/${userId}/`, data);
        return res.data.user;
      } catch (error) {
        throw new Error(error.response?.data?.error || "User update failed");
      }
    },

    async deleteUser(_, userId) {
      try {
        const res = await api.delete(`${USER_API_URL}/${userId}/`);
        return res.data.message;
      } catch (error) {
        throw new Error(error.response?.data?.error || "User deletion failed");
      }
    },

    async deleteUserSessions(_, userId) {
      try {
        const res = await api.delete(`${USER_API_URL}/${userId}/sessions/`);
        return res.data.message;
      } catch (error) {
        throw new Error(
          error.response?.data?.error || "Failed to delete user sessions"
        );
      }
    },
  },
  getters: {
    myUser: (state) => state.me,
    userList: (state) => state.users,
    userSessions: (state) => state.sessions,
    userError: (state) => state.error,
    userLoading: (state) => state.loading,
  },
};
